import type { ComponentSelections } from "./componentSelections";

export interface BundlePrice {
	net: number;
	gross: number;
}

export interface PriceDifference {
	net: number;
	gross: number;
}

export interface ProductPrice {
	net: number;
	gross: number;
}

export function calculateBundlePrice({
	currentBundlePrice,
	previousProductPriceDifference,
	newProductPriceDifference,
}: {
	currentBundlePrice: BundlePrice;
	previousProductPriceDifference: PriceDifference | null;
	newProductPriceDifference: PriceDifference;
}): BundlePrice {

	let net = currentBundlePrice?.net ?? 0;
	let gross = currentBundlePrice?.gross ?? 0;

	if (previousProductPriceDifference) {
		net -= previousProductPriceDifference.net ?? 0;
		gross -= previousProductPriceDifference.gross ?? 0;
	}

	net += newProductPriceDifference?.net ?? 0;
	gross += newProductPriceDifference?.gross ?? 0;

	return { net: Math.max(0, net), gross: Math.max(0, gross) };
}

export function calculateBundlePriceForEmpty({
	currentBundlePrice,
	previousProductPriceDifference,
	defaultProductPrice,
}: {
	currentBundlePrice: BundlePrice;
	previousProductPriceDifference: PriceDifference | null;
	defaultProductPrice: ProductPrice;
}): BundlePrice {
	let net = currentBundlePrice?.net ?? 0;
	let gross = currentBundlePrice?.gross ?? 0;

	if (previousProductPriceDifference) {
		net -= previousProductPriceDifference.net ?? 0;
		gross -= previousProductPriceDifference.gross ?? 0;
	}

	net -= defaultProductPrice?.net ?? 0;
	gross -= defaultProductPrice?.gross ?? 0;

	return { net: Math.max(0, net), gross: Math.max(0, gross) };
}

export function calculateBundlePriceForDefault({
	currentBundlePrice,
	previousProductPriceDifference,
}: {
	currentBundlePrice: BundlePrice;
	previousProductPriceDifference: PriceDifference;
}): BundlePrice {
	return {
		net: Math.max(0, (currentBundlePrice?.net ?? 0) - (previousProductPriceDifference?.net ?? 0)),
		gross: Math.max(0, (currentBundlePrice?.gross ?? 0) - (previousProductPriceDifference?.gross ?? 0)),
	};
}

export interface SelectionWithPrice {
	productId: string | null;
	priceDifference?: { net: number; gross: number };
}

export interface DefaultPriceInfo {
	price: { net: number; gross: number };
	productId: string;
}

export function reconcilePriceFromSelections(
	initialPrice: BundlePrice,
	selections: ComponentSelections,
	defaultPrices: Record<string, DefaultPriceInfo>
): BundlePrice {
	let totalDifferenceNet = 0;
	let totalDifferenceGross = 0;

	for (const [productGroupId, entry] of Object.entries(selections)) {
		if (Array.isArray(entry)) {
			for (const selection of entry) {
				if (selection.productId && selection.priceDifference) {
					totalDifferenceNet += selection.priceDifference.net;
					totalDifferenceGross += selection.priceDifference.gross;
				}
			}
		} else {
			if (!entry.productId) {
				const defaultPriceInfo = defaultPrices[productGroupId];
				if (defaultPriceInfo?.price) {
					totalDifferenceNet -= defaultPriceInfo.price.net;
					totalDifferenceGross -= defaultPriceInfo.price.gross;
				}
			} else if (entry.priceDifference) {
				totalDifferenceNet += entry.priceDifference.net;
				totalDifferenceGross += entry.priceDifference.gross;
			}
		}
	}

	return {
		net: Math.max(0, initialPrice.net + totalDifferenceNet),
		gross: Math.max(0, initialPrice.gross + totalDifferenceGross),
	};
}